// src/utils/supabase.js
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase environment variables are not set. Please configure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.');
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');

// Database queries for the platform
export const supabaseQueries = {
  // Authentication & User Management
  async getUserProfile(userId) {
    const { data, error } = await supabase?.from('user_profiles')?.select(`
        *,
        tenant:tenants(*),
        manager:manager_id(id, full_name, email)
      `)?.eq('id', userId)?.single();
    return { data, error };
  },

  async updateUserProfile(userId, updates) {
    const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.single();
    return { data, error };
  },

  // Tenant Management
  async getTenantInfo(tenantId) {
    const { data, error } = await supabase?.from('tenants')?.select('*')?.eq('id', tenantId)?.single();
    return { data, error };
  },

  async getTenantCredits(tenantId) {
    const { data, error } = await supabase?.from('tenant_credits')?.select('*')?.eq('tenant_id', tenantId)?.single();
    return { data, error };
  },

  // Trip Requests & Approval
  async createTripRequest(tripData) {
    const { data, error } = await supabase?.from('trip_requests')?.insert([tripData])?.select()?.single();
    return { data, error };
  },

  async getTripRequests(tenantId, filters = {}) {
    let query = supabase?.from('trip_requests')?.select(`
        *,
        requester:requester_id(id, full_name, email, department),
        travel_policy:travel_policy_id(name, rules)
      `)?.eq('tenant_id', tenantId);

    if (filters?.status) {
      query = query?.eq('approval_status', filters?.status);
    }
    if (filters?.requesterId) {
      query = query?.eq('requester_id', filters?.requesterId);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  },

  async updateTripRequestStatus(tripId, status, comments) {
    const { data, error } = await supabase?.from('trip_requests')?.update({ 
        approval_status: status,
        updated_at: new Date()?.toISOString()
      })?.eq('id', tripId)?.select()?.single();
    return { data, error };
  },

  // Bookings Management
  async createBooking(bookingData) {
    const { data, error } = await supabase?.from('bookings')?.insert([bookingData])?.select()?.single();
    return { data, error };
  },

  async getBookings(tenantId, filters = {}) {
    let query = supabase?.from('bookings')?.select(`
        *,
        traveler:traveler_id(id, full_name, email),
        agent:agent_id(id, full_name, email),
        trip_request:trip_request_id(title, purpose, destination_to)
      `)?.eq('tenant_id', tenantId);

    if (filters?.travelerId) {
      query = query?.eq('traveler_id', filters?.travelerId);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.bookingType) {
      query = query?.eq('booking_type', filters?.bookingType);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });
    return { data, error };
  },

  // Flight Search & Inventory
  async searchFlights(searchParams) {
    const { origin, destination, departureDate, returnDate } = searchParams;
    
    let query = supabase?.from('flight_inventory')?.select('*')?.eq('origin_airport', origin)?.eq('destination_airport', destination)?.gte('departure_time', departureDate);

    if (returnDate) {
      // For round trips, we'd need to join or make separate queries
      // This is a simplified implementation
    }

    const { data, error } = await query?.order('departure_time');
    return { data, error };
  },

  // Hotel Search & Inventory
  async searchHotels(searchParams) {
    const { location, checkIn, checkOut } = searchParams;
    
    const { data, error } = await supabase?.from('hotel_inventory')?.select('*')?.eq('location_code', location)?.order('star_rating', { ascending: false });

    return { data, error };
  },

  // Agent Operations
  async getAgentQueue(agentId) {
    const { data, error } = await supabase?.from('agent_queues')?.select(`
        *,
        booking:booking_id(
          id,
          booking_reference,
          booking_type,
          total_cost,
          status,
          traveler:traveler_id(full_name, email)
        )
      `)?.eq('agent_id', agentId)?.is('completed_at', null)?.order('priority_level', { ascending: false });

    return { data, error };
  },

  async assignBookingToAgent(bookingId, agentId, priority = 1) {
    const { data, error } = await supabase?.from('agent_queues')?.insert([{
        agent_id: agentId,
        booking_id: bookingId,
        priority_level: priority,
        sla_deadline: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours
      }])?.select()?.single();

    return { data, error };
  },

  // Notifications
  async createNotification(notificationData) {
    const { data, error } = await supabase?.from('notifications')?.insert([notificationData])?.select()?.single();
    return { data, error };
  },

  async getUserNotifications(userId, limit = 20) {
    const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

    return { data, error };
  },

  async markNotificationRead(notificationId) {
    const { data, error } = await supabase?.from('notifications')?.update({ 
        is_read: true,
        read_at: new Date()?.toISOString()
      })?.eq('id', notificationId)?.select()?.single();

    return { data, error };
  },

  // Travel Policies
  async getTravelPolicies(tenantId) {
    const { data, error } = await supabase?.from('travel_policies')?.select('*')?.eq('tenant_id', tenantId)?.eq('is_active', true)?.order('created_at', { ascending: false });

    return { data, error };
  },

  // Credit Management
  async getCreditTransactions(tenantId, limit = 50) {
    const { data, error } = await supabase?.from('credit_transactions')?.select(`
        *,
        user:user_id(full_name, email),
        processed_by_user:processed_by(full_name, email)
      `)?.eq('tenant_id', tenantId)?.order('created_at', { ascending: false })?.limit(limit);

    return { data, error };
  }
};

// Real-time subscriptions
export const setupRealtimeSubscription = (table, callback, filter) => {
  const channel = supabase?.channel(`${table}-changes`)?.on(
      'postgres_changes',
      { 
        event: '*', 
        schema: 'public', 
        table: table,
        ...filter
      },
      callback
    )?.subscribe();

  return channel;
};